import React, { useState } from "react";
import "./admin.css";
import { useTranslation } from "react-i18next";
import { LogOut, Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";

/* ===== FIREBASE ===== */
import { auth, db } from "../firebase";
import { 
  signInWithEmailAndPassword, 
  signOut,
  setPersistence, 
  browserSessionPersistence, 
  sendPasswordResetEmail 
} from "firebase/auth";
import { doc, getDoc, updateDoc } from "firebase/firestore";

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // 1. SLAPTAŽODŽIO ATSTATYMAS
  const handleForgotPassword = async () => {
    if (!email) return setError("Please enter your email first.");
    try {
      await sendPasswordResetEmail(auth, email);
      setInfo("Password reset link sent! Check your inbox.");
      setError("");
    } catch (err) { setError("Could not send reset link."); }
  };

  // 2. PRISIJUNGIMO LOGIKA
  const handleLogin = async (e) => {
    e.preventDefault();
    setError(""); setInfo(""); setLoading(true);

    // Išvalome el. paštą (nuimame tarpus)
    const cleanEmail = email.trim().toLowerCase();

    try {
      // Nustatome sesijos trukmę
      await setPersistence(auth, browserSessionPersistence);
      
      // A. PIRMIAUSIA PRISIJUNGIAME (Auth sistema)
      const userCredential = await signInWithEmailAndPassword(auth, cleanEmail, password);
      const user = userCredential.user;

      // B. DABAR, KAI JAU ESAME PRISIJUNGĘ, TIKRINAME FIRESTORE
      // (Taisyklės dabar leis mums perskaityti savo dokumentą)
      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();

        // Patikra: Ar paskyra užblokuota?
        if (userData.isLocked) {
          setError("❌ ACCOUNT LOCKED! Please contact Master Admin.");
          await signOut(auth); // Išmetame, nes užblokuotas
          setLoading(false);
          return;
        }

        // Sėkmė - įrašome naują sesiją
        const sessionId = Date.now().toString();
        localStorage.setItem("activeSessionId", sessionId);
        
        await updateDoc(userRef, { 
          loginAttempts: 0, // Nunuliname bandymus po sėkmės
          activeSessionId: sessionId
        });

        navigate("/admin");
      } else {
        // Jei tai sena paskyra be Firestore dokumento
        navigate("/admin");
      }

    } catch (authErr) {
      console.error("Login error code:", authErr.code);
      
      // Paprasta klaidų apdorojimo sistema
      if (authErr.code === "auth/invalid-credential" || authErr.code === "auth/wrong-password" || authErr.code === "auth/user-not-found") {
        setError("Invalid email or password.");
      } else if (authErr.code === "auth/too-many-requests") {
        setError("Too many attempts. Try again later.");
      } else {
        setError("Login failed. Check your connection.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExit = async () => {
    await signOut(auth);
    navigate("/");
  };

  return (
    <div className="admin-login-container">
      <div className="admin-login-card gold-glow">
        <div style={{ marginBottom: '20px' }}>
          <img src={require("../assets/bf_logo.png")} alt="Logo" style={{ width: '80px' }} />
        </div>
        <h2 className="admin-login-title">Admin Access</h2>

        {error && <div className="status-msg error">{error}</div>}
        {info && <div className="status-msg info">{info}</div>}

        <form onSubmit={handleLogin} className="admin-login-form">
          <div className="input-group">
            <input 
              type="email" 
              className="admin-input" 
              required 
              placeholder="Email" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
            />
          </div>

          <div className="input-group">
            <div className="password-wrapper">
              <input 
                type={showPassword ? "text" : "password"} 
                className="admin-input" 
                required 
                placeholder="Password" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
              />
              <button 
                type="button" 
                className="eye-btn" 
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <div className="forgot-password-link">
            <span onClick={handleForgotPassword}>Forgot password?</span>
          </div>

          <div className="admin-action-buttons">
            <button type="button" className="login-exit-btn" onClick={handleExit}>
              <LogOut size={18} />
            </button>
            <button type="submit" className="login-submit-btn" disabled={loading}>
              {loading ? "..." : "LOGIN"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminLoginPage;